import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, todayIso } from "@/lib/entries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FlaskConical, Search, X, CheckCircle2, AlertTriangle, Settings } from "lucide-react";

function daysOpen(entryDateIso) {
  const d = new Date(entryDateIso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

export default function LabWorkSheet() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [threshold, setThreshold] = useState(7);
  // single-mark dialog
  const [receiving, setReceiving] = useState(null); // { ids: [], names: [] } | null
  const [receivedDate, setReceivedDate] = useState(todayIso());
  const [followupNote, setFollowupNote] = useState("Lab work received");
  const [saving, setSaving] = useState(false);
  // batch selection
  const [selected, setSelected] = useState(new Set());
  // settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState(7);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const [labs, settings] = await Promise.all([
        api.get("/sheets/labs"),
        api.get("/settings"),
      ]);
      setRows(labs.data);
      setThreshold(settings.data.lab_overdue_days ?? 7);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReceiveOne = (row) => {
    setReceiving({ ids: [row.id], names: [row.patient_name] });
    setReceivedDate(todayIso());
    setFollowupNote("Lab work received");
  };

  const openReceiveBatch = () => {
    const ids = [...selected];
    if (!ids.length) return;
    const names = rows.filter((r) => selected.has(r.id)).map((r) => r.patient_name);
    setReceiving({ ids, names });
    setReceivedDate(todayIso());
    setFollowupNote("Lab work received");
  };

  const undoReceived = async (labId) => {
    try {
      await api.post(`/entries/${labId}/lab-received/undo`);
      toast.success("Reverted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmReceive = async () => {
    if (!receivedDate) { toast.error("Please pick the received date"); return; }
    const ids = receiving?.ids || [];
    if (!ids.length) return;
    setSaving(true);
    try {
      if (ids.length === 1) {
        await api.post(`/entries/${ids[0]}/lab-received`, {
          received_date: receivedDate, followup_note: followupNote,
        });
        toast.success(`Marked received on ${fmtDate(receivedDate)} · added to Follow-ups`, {
          action: { label: "Undo", onClick: () => undoReceived(ids[0]) },
          duration: 8000,
        });
      } else {
        const { data } = await api.post(`/lab/bulk-received`, {
          entry_ids: ids, received_date: receivedDate, followup_note: followupNote,
        });
        toast.success(`Marked ${data.processed} received · ${data.processed} follow-ups added`);
      }
      setReceiving(null);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/settings", { lab_overdue_days: Number(thresholdDraft) });
      setThreshold(data.lab_overdue_days);
      toast.success("Overdue threshold updated");
      setSettingsOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSettings(false); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((e) => e.patient_name.toLowerCase().includes(s));
  }, [rows, q]);

  const overdueCount = useMemo(
    () => rows.filter((e) => daysOpen(e.entry_date) > threshold).length,
    [rows, threshold]
  );

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someSelected = filtered.some((r) => selected.has(r.id));

  const toggleOne = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (allSelected) {
      const n = new Set(selected);
      filtered.forEach((r) => n.delete(r.id));
      setSelected(n);
    } else {
      const n = new Set(selected);
      filtered.forEach((r) => n.add(r.id));
      setSelected(n);
    }
  };

  return (
    <div className="px-6 lg:px-10 pt-4 lg:pt-6 pb-3 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center">
          <FlaskConical size={18} />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Awaiting lab delivery</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">Lab Work</h1>
        </div>
        {overdueCount > 0 && (
          <Badge className="bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-50"
                 data-testid="labs-overdue-badge">
            <AlertTriangle size={12} className="mr-1" /> {overdueCount} overdue (&gt;{threshold}d)
          </Badge>
        )}
        <Button size="sm" variant="outline"
                onClick={() => { setThresholdDraft(threshold); setSettingsOpen(true); }}
                data-testid="labs-settings-button">
          <Settings size={14} className="mr-1.5" /> Settings
        </Button>
      </div>

      <Card className="mt-3 border-slate-200 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 pr-9" placeholder="Search patient name in this sheet..."
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="labs-search-input" />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="labs-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center">
              <X size={14} />
            </button>
          )}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="labs-batch-toolbar">
            <span className="text-sm text-slate-600">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="labs-clear-selection">Clear</Button>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={openReceiveBatch} data-testid="labs-batch-receive">
              Mark {selected.size} received
            </Button>
          </div>
        )}
      </Card>

      <Card className="mt-3 mb-3 border-slate-200 overflow-hidden [&>div]:max-h-[calc(100vh-190px)] [&>div]:overflow-auto" data-testid="labs-sheet">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="labs-empty">
            {q ? `No lab entries matching "${q}".` : "No open lab work."}
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(226,232,240,1)]">
              <TableRow>
                <TableHead className="w-14">S.No.</TableHead>
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                            aria-label="Select all" data-testid="labs-select-all"
                            data-state={allSelected ? "checked" : someSelected ? "indeterminate" : "unchecked"} />
                </TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Ordered on</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Days open</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e, i) => {
                const days = daysOpen(e.entry_date);
                const overdue = days > threshold;
                return (
                  <TableRow key={e.id} data-testid={`lab-row-${e.id}`}
                            className={overdue ? "bg-amber-50/40" : ""}>
                    <TableCell className="text-slate-500 text-sm w-14" data-testid={`lab-sno-${e.id}`}>{i + 1}</TableCell>
                    <TableCell>
                      <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleOne(e.id)}
                                aria-label={`Select ${e.patient_name}`} data-testid={`lab-select-${e.id}`} />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      <button type="button" onClick={() => setQ(e.patient_name)}
                              data-testid={`lab-name-${e.id}`}
                              className="hover:text-violet-700 text-left">
                        {e.patient_name}
                      </button>
                    </TableCell>
                    <TableCell className="text-slate-700">{fmtDate(e.entry_date)}</TableCell>
                    <TableCell className="text-slate-600 max-w-xs truncate">{e.notes || "—"}</TableCell>
                    <TableCell className={overdue ? "text-amber-700 font-semibold" : "text-slate-700"}>
                      {days}d
                      {overdue && <AlertTriangle size={12} className="inline ml-1 -mt-0.5" />}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Link to={`/entries/${e.id}`}>
                        <Button size="sm" variant="outline" data-testid={`lab-open-${e.id}`}>Open</Button>
                      </Link>
                      <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white"
                              onClick={() => openReceiveOne(e)} data-testid={`lab-received-${e.id}`}>
                        Mark received
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!receiving} onOpenChange={(v) => !v && setReceiving(null)}>
        <DialogContent className="sm:max-w-md" data-testid="lab-received-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CheckCircle2 size={18} className="text-violet-600" /> Lab work received
            </DialogTitle>
            <DialogDescription>
              {receiving && receiving.ids.length === 1
                ? <>Pick the received date for <span className="font-medium text-slate-800">{receiving.names[0]}</span>.</>
                : <>Marking <span className="font-medium text-slate-800">{receiving?.ids.length} patients</span> received on the same date.</>}
              {" "}A follow-up entry will be added on that date with the note below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Received on</Label>
              <Input type="date" value={receivedDate}
                     onChange={(e) => setReceivedDate(e.target.value)}
                     data-testid="lab-received-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Follow-up note</Label>
              <Textarea rows={2} value={followupNote}
                        onChange={(e) => setFollowupNote(e.target.value)}
                        placeholder="Lab work received"
                        data-testid="lab-followup-note-input" />
              <p className="text-xs text-slate-500">Shown on the follow-up entry. Defaults to &quot;Lab work received&quot;.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReceiving(null)}
                    data-testid="lab-received-cancel">Cancel</Button>
            <Button type="button" disabled={saving}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={confirmReceive} data-testid="lab-received-confirm">
              {saving ? "Saving..." : "Confirm received"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="labs-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Settings size={18} className="text-slate-700" /> Lab Work settings
            </DialogTitle>
            <DialogDescription>Adjust when a lab work entry is considered overdue.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Overdue after (days)</Label>
            <Input type="number" min={1} max={365}
                   value={thresholdDraft}
                   onChange={(e) => setThresholdDraft(e.target.value)}
                   data-testid="labs-threshold-input" />
            <p className="text-xs text-slate-500">Lab entries open for more than this many days get an amber warning on this sheet and the Dashboard.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)}
                    data-testid="labs-settings-cancel">Cancel</Button>
            <Button type="button" disabled={savingSettings}
                    onClick={saveSettings} data-testid="labs-settings-save"
                    className="bg-slate-900 hover:bg-slate-800 text-white">
              {savingSettings ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
